import { calcPagamento } from "./pagamento";
import { calcCustoPedido } from "./calcCusto";
import { normalizarStatus } from "../constants/statusPedido";

/*
 * Métricas automáticas derivadas de pedidos + falhas.
 * Tudo é calculado a partir do Firebase (fonte única) — nada de lançamento manual.
 */

export function parseDataPedido(p) {
  if (p?.created_at) {
    try { if (p.created_at.toDate) return p.created_at.toDate(); const d = new Date(p.created_at); if (!isNaN(d)) return d; } catch {}
  }
  if (p?.data) { const [d, m, y] = String(p.data).split("/"); const dt = new Date(`${y}-${m}-${d}T12:00:00`); if (!isNaN(dt)) return dt; }
  return null;
}

export function pesoPedido(p) {
  let s = (p?.filamentos_usados || []).reduce((a, f) => a + (parseFloat(f.peso) || 0), 0);
  for (const it of (p?.itens || [])) s += (it.filamentos_usados || []).reduce((a, f) => a + (parseFloat(f.peso) || 0), 0) * (parseInt(it.qtd) || 1);
  return s;
}

export function inicioPeriodo(periodo, ref = new Date()) {
  const d = new Date(ref); d.setHours(0, 0, 0, 0);
  if (periodo === "hoje")   return d;
  if (periodo === "semana") { const x = new Date(d); x.setDate(x.getDate() - 6); return x; }
  if (periodo === "mes")    return new Date(d.getFullYear(), d.getMonth(), 1);
  if (periodo === "ano")    return new Date(d.getFullYear(), 0, 1);
  return new Date(0);
}

const IMPRESSOS = ["imprimindo", "concluido", "pronto_entrega", "entregue"];
export function jaImprimiu(p) { return IMPRESSOS.includes(normalizarStatus(p?.status)) || p?.estoque_baixado === true; }

// Status da FILA DE IMPRESSÃO que contam como produção realizada no período
const FILA_IMPRESSOS = ["imprimindo", "pronto", "concluido"];

// Horas e peso de um item da fila: usa o valor do próprio item e,
// se faltar, herda do pedido vinculado (mesma lógica da aba Fila de Impressão).
export function horasPesoItemFila(item, pedidos = []) {
  let horas = parseFloat(item?.horas) || 0;
  let peso  = parseFloat(item?.peso)  || 0;
  if ((!horas || !peso) && item?.pedidoId) {
    const p = pedidos.find(x => x.id === item.pedidoId);
    if (p) { if (!horas) horas = parseFloat(p.horas_impressao) || 0; if (!peso) peso = pesoPedido(p); }
  }
  return { horas, peso };
}

// Cada pagamento vira um evento de receita datado (parciais contam na data em que entraram)
export function eventosReceita(p) {
  const evts = [];
  if (Array.isArray(p?.pagamentos) && p.pagamentos.length) {
    for (const pg of p.pagamentos) { const v = parseFloat(pg.valor) || 0; if (v > 0) evts.push({ valor: v, data: pg.em ? new Date(pg.em) : parseDataPedido(p) }); }
  } else {
    const { recebido } = calcPagamento(p);
    if (recebido > 0) evts.push({ valor: recebido, data: parseDataPedido(p) });
  }
  return evts;
}

export function receitaEntre(pedidos, ini, fim) {
  let total = 0;
  for (const p of pedidos) for (const ev of eventosReceita(p)) {
    if (ev.data && ev.data >= ini && (!fim || ev.data < fim)) total += ev.valor;
  }
  return total;
}

export function calcMetricas(pedidos, falhas, periodo, impressoras = [], produtos = [], fila = []) {
  const ini = inicioPeriodo(periodo);
  const noPeriodo = (dt) => dt && dt >= ini;
  const ativos = pedidos.filter(p => normalizarStatus(p.status) !== "cancelado");

  const receita = receitaEntre(pedidos, ini, null);

  const criados    = ativos.filter(p => noPeriodo(parseDataPedido(p)));
  const concluidos = ativos.filter(p => ["concluido", "pronto_entrega", "entregue"].includes(normalizarStatus(p.status)) && noPeriodo(parseDataPedido(p)));

  // Horas impressas e filamento usado vêm da FILA DE IMPRESSÃO (fonte que o
  // usuário preenche): itens já impressos (Imprimindo/Pronto/Concluído) no período.
  const filaImpressa = (fila || []).filter(it => FILA_IMPRESSOS.includes(it.status) && noPeriodo(parseDataPedido(it)));
  const horas     = filaImpressa.reduce((s, it) => s + horasPesoItemFila(it, pedidos).horas, 0);
  const filamento = filaImpressa.reduce((s, it) => s + horasPesoItemFila(it, pedidos).peso, 0);
  const custo     = concluidos.reduce((s, p) => s + calcCustoPedido(p, impressoras, produtos).total, 0);
  const lucro     = receita - custo;

  const falhasPeriodo  = (falhas || []).filter(f => { const dt = f.data ? new Date(f.data) : null; return noPeriodo(dt); });
  const nFalhas        = falhasPeriodo.length;
  // Sucessos vêm da MESMA fonte das falhas (a fila): impressões concluídas no período.
  const sucessos       = (fila || []).filter(it => it.status === "concluido" && noPeriodo(parseDataPedido(it))).length;
  const taxaSucesso    = (sucessos + nFalhas) > 0 ? (sucessos / (sucessos + nFalhas)) * 100 : null;
  const filamentoPerdido = falhasPeriodo.reduce((s, f) => s + (parseFloat(f.filamento_perdido_g) || 0), 0);
  const valorPerdido     = falhasPeriodo.reduce((s, f) => s + (parseFloat(f.valor_perdido) || 0), 0);

  return {
    receita, lucro, custo,
    recebidos: criados.length, concluidos: concluidos.length,
    horas, filamento, nFalhas, taxaSucesso, filamentoPerdido, valorPerdido,
  };
}

// Indicadores de produção (sobre todos os pedidos não cancelados)
export function indicadoresProducao(pedidos) {
  const prodCount = {}, corPeso = {}, matPeso = {};
  const add = (obj, k, v) => { if (k) obj[k] = (obj[k] || 0) + v; };
  for (const p of pedidos) {
    if (normalizarStatus(p.status) === "cancelado") continue;
    if ((p.itens || []).length) for (const it of p.itens) add(prodCount, it.nome, parseInt(it.qtd) || 1);
    else if (p.produto) add(prodCount, p.produto, 1);
    for (const f of (p.filamentos_usados || [])) { add(corPeso, f.nome || f.cor, parseFloat(f.peso) || 0); add(matPeso, f.material, parseFloat(f.peso) || 0); }
    for (const it of (p.itens || [])) for (const f of (it.filamentos_usados || [])) { add(corPeso, f.nome || f.cor, (parseFloat(f.peso) || 0) * (parseInt(it.qtd) || 1)); add(matPeso, f.material, (parseFloat(f.peso) || 0) * (parseInt(it.qtd) || 1)); }
  }
  const top = (obj) => { const e = Object.entries(obj).sort((a, b) => b[1] - a[1])[0]; return e ? { nome: e[0], valor: e[1] } : null; };
  return { produtoMaisVendido: top(prodCount), corMaisUsada: top(corPeso), filamentoMaisUsado: top(matPeso) };
}
